<script lang="ts" setup>
import { breakpointsTailwind } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import type {
  UseElementBreakpointsReturn,
} from '@nora-monorepo/ui-shared';
import {
  useDeviceStore,
  useElementBreakpoints,
  useGenericUserStore,
} from '@nora-monorepo/ui-shared';

import AllModals from '#nora-chat-ui/components/AllModals.vue';
import { useConversationDetailsModalProvider } from '#nora-chat-ui/components/modals/new-conversation/conversation-details-modal-context';
import { ConversationModalActionState } from '#nora-chat-ui/components/modals/new-conversation/conversation-modal-types';
import ConversationModal from '#nora-chat-ui/components/modals/new-conversation/ConversationModal.vue';
import OpenConversations from '#nora-chat-ui/components/open-conversations/OpenConversations.vue';
import ChatSidebar from '#nora-chat-ui/components/sidebar/ChatSidebar.vue';
import { useAppStore } from '#nora-chat-ui/stores/app-store';
import { useChatSseStore } from '#nora-chat-ui/stores/chat-sse-store';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useFaviconStore } from '#nora-chat-ui/stores/favicon-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

interface ChatProps {
  responsiveToContainer?: boolean;
}

const { responsiveToContainer } = defineProps<ChatProps>();

const appStore = useAppStore();
const chatUserStore = useChatUserStore();
const genericUserStore = useGenericUserStore();
const sseStore = useChatSseStore();
const deviceStore = useDeviceStore();
const conversationStore = useConversationStore();
const faviconStore = useFaviconStore();
const conversationDetailsModal = useConversationDetailsModalProvider();

const { userIsLoading } = storeToRefs(genericUserStore);
const { showSideBarMobile } = storeToRefs(appStore);
const { getOpenConversations } = storeToRefs(conversationStore);
const { isMobile } = storeToRefs(deviceStore);

const chatRef = ref(null);
const breakpoints = useElementBreakpoints(chatRef, breakpointsTailwind);

if (responsiveToContainer) {
  watch(
    breakpoints.current(),
    () => {
      deviceStore.setBreakpoints(
        breakpoints as UseElementBreakpointsReturn<string>,
      );
    },
    { immediate: true },
  );
}

const showSidebar = computed(() => {
  if (!isMobile.value) {
    return true;
  }

  if (getOpenConversations.value.length < 1) {
    return true;
  }

  return showSideBarMobile.value;
});

const showOpenConversations = computed(() => {
  if (!isMobile.value) {
    return true;
  }

  if (getOpenConversations.value.length >= 1) {
    return true;
  }

  return !showSideBarMobile.value;
});

const isReady = ref(false);

async function bootstrap() {
  faviconStore.bootstrap();
  conversationStore.bootstrap();

  await chatUserStore.bootstrap(async () => {
    await conversationStore.loadConversations();
    await sseStore.startListening();

    isReady.value = true;
  });
}

if (!userIsLoading.value && !isReady.value) {
  bootstrap();
}
else {
  watch(userIsLoading, async () => {
    await bootstrap();
  });
}
</script>

<template>
  <span ref="chatRef" class="w-full h-full">
    <span v-if="isReady">
      <div id="nora-chat-body" class="relative h-full">
        <div class="flex h-full w-full">
          <ChatSidebar v-if="showSidebar" />
          <OpenConversations v-if="showOpenConversations" />
        </div>
      </div>
      <ConversationModal
        :is-open="conversationDetailsModal.isOpen.value"
        :mode="ConversationModalActionState.Edit"
        :conversation="conversationDetailsModal.conversation.value"
        :read-only="conversationDetailsModal.readOnly.value"
        @close="conversationDetailsModal.close"
      />
      <AllModals />
    </span>
  </span>
</template>
